import React, { useMemo, useId } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_POSITIONS = {
  B: { x: 300, y: 30 }, K: { x: 300, y: 100 }, Zh: { x: 300, y: 165 },
  D: { x: 175, y: 245 }, Z: { x: 425, y: 245 },
  I: { x: 85, y: 285 }, L: { x: 515, y: 285 },
  G: { x: 300, y: 315 }, M: { x: 300, y: 415 },
  A: { x: 25, y: 305 }, V: { x: 575, y: 305 },
  E: { x: 300, y: 560 },
  HRT: { x: 300, y: 240 }, Man: { x: 300, y: 370 },
  SUN: { x: 520, y: 50 },
};

const DEFAULT_COLORS = {
  B: '#a855f7', K: '#6366f1', Zh: '#3b82f6',
  D: '#4f46e5', Z: '#4f46e5', I: '#0ea5e9', L: '#0ea5e9',
  G: '#22c55e', M: '#eab308', A: '#f97316', V: '#f97316', E: '#ef4444',
  HRT: '#10b981', Man: '#f59e0b', SUN: '#fbbf24',
};

const DEFAULT_LABELS = {
  B: 'Б', D: 'Д', Zh: 'Ж', Z: 'З', I: 'И', L: 'Л',
  G: 'Г', M: 'М', A: 'А', K: 'К', V: 'В', E: 'Е',
  HRT: 'HRT', Man: 'Man', SUN: '☀',
};

const DEFAULT_CONNECTIONS = [
  ['B', 'D'], ['B', 'Zh'], ['B', 'Z'],
  ['D', 'Zh'], ['D', 'I'], ['D', 'A'],
  ['Zh', 'Z'], ['Zh', 'I'], ['Zh', 'L'], ['Zh', 'G'],
  ['Z', 'L'], ['Z', 'V'],
  ['I', 'G'], ['I', 'M'], ['I', 'A'],
  ['L', 'G'], ['L', 'V'],
  ['G', 'M'], ['G', 'K'],
  ['M', 'A'], ['M', 'K'],
  ['A', 'K'], ['A', 'E'],
  ['K', 'V'], ['K', 'E'],
  ['V', 'E'],
];

const CORE_NODE_IDS = ['B', 'K', 'Zh', 'D', 'Z', 'I', 'L', 'G', 'M', 'A', 'V', 'E', 'HRT', 'Man', 'SUN'];

// Build SVG path/shape for a given shape type at (cx, cy) with radius r
const shapePoints = (shape, cx, cy, r) => {
  if (shape === 'square') {
    return { type: 'rect', attrs: { x: cx - r, y: cy - r, width: r * 2, height: r * 2, rx: r * 0.12 } };
  }
  if (shape === 'diamond') {
    const pts = [[cx, cy - r], [cx + r, cy], [cx, cy + r], [cx - r, cy]];
    return { type: 'polygon', attrs: { points: pts.map(p => p.join(',')).join(' ') } };
  }
  if (shape === 'triangle') {
    const pts = [[cx, cy - r], [cx + r * 0.866, cy + r * 0.5], [cx - r * 0.866, cy + r * 0.5]];
    return { type: 'polygon', attrs: { points: pts.map(p => p.join(',')).join(' ') } };
  }
  if (shape === 'hexagon') {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    return { type: 'polygon', attrs: { points: pts.map(p => p.join(',')).join(' ') } };
  }
  if (shape === 'star') {
    const pts = [];
    const rOuter = r;
    const rInner = r * 0.42;
    for (let i = 0; i < 10; i++) {
      const a = (Math.PI / 5) * i - Math.PI / 2;
      const rad = i % 2 === 0 ? rOuter : rInner;
      pts.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]);
    }
    return { type: 'polygon', attrs: { points: pts.map(p => p.join(',')).join(' ') } };
  }
  if (shape === 'heart') {
    // Heart path centered at cx,cy, size r
    const s = r;
    const d = `M ${cx} ${cy + s * 0.55}
      C ${cx - s * 1.1} ${cy - s * 0.05}, ${cx - s * 0.8} ${cy - s * 0.95}, ${cx} ${cy - s * 0.35}
      C ${cx + s * 0.8} ${cy - s * 0.95}, ${cx + s * 1.1} ${cy - s * 0.05}, ${cx} ${cy + s * 0.55} Z`;
    return { type: 'path', attrs: { d } };
  }
  if (shape === 'door') {
    // Arched doorway: rectangle with rounded top
    const w = r * 1.5;
    const h = r * 2.2;
    const top = cy - h / 2;
    const bot = cy + h / 2;
    const left = cx - w / 2;
    const right = cx + w / 2;
    const arcR = w / 2;
    const d = `M ${left} ${bot}
      L ${left} ${top + arcR}
      A ${arcR} ${arcR} 0 0 1 ${right} ${top + arcR}
      L ${right} ${bot} Z`;
    return { type: 'path', attrs: { d } };
  }
  // circle default
  return { type: 'circle', attrs: { cx, cy, r } };
};

// Render a shape element with the given fill/filter/className
const NodeShape = ({ shape, cx, cy, r, fill, filter, className, stroke, strokeWidth, opacity, ...rest }) => {
  const s = shapePoints(shape || 'circle', cx, cy, r);
  const Component = s.type === 'rect' ? 'rect' : s.type === 'polygon' ? 'polygon' : s.type === 'path' ? 'path' : 'circle';
  return (
    <Component
      {...s.attrs}
      fill={fill}
      filter={filter}
      className={className}
      stroke={stroke}
      strokeWidth={strokeWidth}
      opacity={opacity}
      {...rest}
    />
  );
};

const HumanFigure = () => (
  <g className="human-wireframe" opacity="0.1" stroke="rgba(147,197,253,1)" strokeWidth="0.7" fill="none">
    <circle cx="300" cy="85" r="32" />
    <circle cx="300" cy="85" r="22" strokeDasharray="3 4" />
    <line x1="300" y1="117" x2="300" y2="145" />
    <line x1="180" y1="165" x2="420" y2="165" />
    <line x1="240" y1="165" x2="230" y2="420" />
    <line x1="360" y1="165" x2="370" y2="420" />
    <line x1="230" y1="420" x2="370" y2="420" />
    <line x1="240" y1="165" x2="300" y2="230" />
    <line x1="360" y1="165" x2="300" y2="230" />
    <line x1="300" y1="230" x2="235" y2="290" />
    <line x1="300" y1="230" x2="365" y2="290" />
    <line x1="235" y1="290" x2="365" y2="290" />
    <line x1="235" y1="290" x2="300" y2="360" />
    <line x1="365" y1="290" x2="300" y2="360" />
    <line x1="300" y1="360" x2="232" y2="420" />
    <line x1="300" y1="360" x2="368" y2="420" />
    <line x1="180" y1="165" x2="100" y2="250" />
    <line x1="100" y1="250" x2="30" y2="310" />
    <line x1="420" y1="165" x2="500" y2="250" />
    <line x1="500" y1="250" x2="570" y2="310" />
    <line x1="260" y1="420" x2="220" y2="650" />
    <line x1="340" y1="420" x2="380" y2="650" />
    <line x1="300" y1="145" x2="300" y2="420" strokeDasharray="4 6" opacity="0.4" />
    <circle cx="300" cy="290" r="50" strokeDasharray="2 5" opacity="0.25" />
  </g>
);

// Build an SVG path string for the connection according to style
const buildConnectionPath = (x1, y1, x2, y2, style) => {
  if (style === 'curved') {
    const dx = x2 - x1, dy = y2 - y1;
    // Perpendicular offset for a subtle curve
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
    const len = Math.sqrt(dx * dx + dy * dy);
    const curveAmt = Math.min(35, len * 0.25);
    const nx = -dy / (len || 1);
    const ny = dx / (len || 1);
    const cx = mx + nx * curveAmt;
    const cy = my + ny * curveAmt;
    return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
  }
  if (style === 'wavy') {
    // Wavy: sinusoidal pattern along the line using multiple quadratic curves
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 10) return `M ${x1} ${y1} L ${x2} ${y2}`;
    const nx = -dy / len;
    const ny = dx / len;
    const segments = Math.max(3, Math.floor(len / 40));
    const amp = 6;
    let d = `M ${x1} ${y1}`;
    for (let i = 1; i <= segments; i++) {
      const t1 = (i - 0.5) / segments;
      const t2 = i / segments;
      const sign = i % 2 === 0 ? -1 : 1;
      const px = x1 + dx * t1 + nx * amp * sign;
      const py = y1 + dy * t1 + ny * amp * sign;
      const ex = x1 + dx * t2;
      const ey = y1 + dy * t2;
      d += ` Q ${px} ${py} ${ex} ${ey}`;
    }
    return d;
  }
  // straight & dashed share the same path
  return `M ${x1} ${y1} L ${x2} ${y2}`;
};

// Nerve-animated connection with speed & style support
const NerveLine = ({ x1, y1, x2, y2, index, color1, color2, speed = 1, style = 'straight', isGrouped = false }) => {
  const dx = x2 - x1, dy = y2 - y1;
  const baseLen = Math.sqrt(dx * dx + dy * dy);
  // For wavy, path is longer; approximate with factor 1.15
  const len = style === 'wavy' ? baseLen * 1.15 : (style === 'curved' ? baseLen * 1.05 : baseLen);
  const speedFactor = 1 / Math.max(0.1, speed);
  const dur1 = (1.5 + (index % 5) * 0.4) * speedFactor;
  const dur2 = (2.0 + (index % 7) * 0.3) * speedFactor;
  const delay1 = ((index * 0.2) % 3) * speedFactor;
  const delay2 = ((index * 0.35) % 2.5) * speedFactor;
  const gradId = `nerve-grad-${index}`;
  const path = buildConnectionPath(x1, y1, x2, y2, style);

  // Stroke widths boost when in active group
  const baseStrokeW = isGrouped ? 3.2 : 2;
  const trailStrokeW = isGrouped ? 2.4 : 1.5;
  const ambientOpacity = isGrouped ? 0.18 : 0.06;

  // For dashed style, use static heavy dash pattern with slower animated offset, NO particle
  if (style === 'dashed') {
    return (
      <g>
        <defs>
          <linearGradient id={gradId} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={color1}>
              <animate attributeName="stop-color"
                values={`${color1};${color2};${color1}`}
                dur={`${dur1 + 2}s`} repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor={color2}>
              <animate attributeName="stop-color"
                values={`${color2};${color1};${color2}`}
                dur={`${dur2 + 2}s`} repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        <path d={path} fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={baseStrokeW} strokeLinecap="round"
          strokeDasharray="8 8"
          opacity={isGrouped ? 0.95 : 0.75}>
          <animate attributeName="stroke-dashoffset"
            from="0" to="-32"
            dur={`${dur1}s`} repeatCount="indefinite" />
        </path>
      </g>
    );
  }

  return (
    <g>
      <defs>
        <linearGradient id={gradId} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={color1}>
            <animate attributeName="stop-color"
              values={`${color1};${color2};${color1}`}
              dur={`${dur1 + 2}s`} repeatCount="indefinite" />
          </stop>
          <stop offset="100%" stopColor={color2}>
            <animate attributeName="stop-color"
              values={`${color2};${color1};${color2}`}
              dur={`${dur2 + 2}s`} repeatCount="indefinite" />
          </stop>
        </linearGradient>
      </defs>

      {/* Soft glow underlayer when grouped */}
      {isGrouped && (
        <path d={path} fill="none"
          stroke={color1} strokeWidth={baseStrokeW * 3} strokeLinecap="round"
          opacity="0.18" filter="url(#particle-glow)" />
      )}

      <path d={path} fill="none"
        stroke={isGrouped ? color1 : "rgba(147,197,253,0.06)"}
        strokeOpacity={isGrouped ? 0.4 : 1}
        strokeWidth={isGrouped ? baseStrokeW * 1.5 : 3}
        strokeLinecap="round" />

      <path d={path} fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={baseStrokeW} strokeLinecap="round"
        strokeDasharray={`${len * 0.12} ${len * 0.88}`}
        opacity={isGrouped ? 0.95 : 0.7}>
        <animate attributeName="stroke-dashoffset"
          from={len} to={0}
          dur={`${dur1}s`} begin={`${delay1}s`}
          repeatCount="indefinite" />
      </path>

      <path d={path} fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={trailStrokeW} strokeLinecap="round"
        strokeDasharray={`${len * 0.08} ${len * 0.92}`}
        opacity={isGrouped ? 0.7 : 0.4}>
        <animate attributeName="stroke-dashoffset"
          from={0} to={len}
          dur={`${dur2}s`} begin={`${delay2}s`}
          repeatCount="indefinite" />
      </path>

      {/* Particle travels along path */}
      <circle r={isGrouped ? 4 : 2.5} fill="white" opacity={isGrouped ? 0.95 : 0.6} filter="url(#particle-glow)">
        <animateMotion path={path} dur={`${dur1 + 0.5}s`} begin={`${delay1}s`} repeatCount="indefinite" />
        <animate attributeName="opacity"
          values={isGrouped ? "0;1;1;0" : "0;0.7;0.7;0"} dur={`${dur1 + 0.5}s`}
          begin={`${delay1}s`} repeatCount="indefinite" />
      </circle>
    </g>
  );
};

// Paradise mode visual overlay — golden halo ring + upward light beam.
// Renders BEHIND the node shape so the node fill stays on top.
const ParadiseOverlay = ({ cx, cy, r }) => {
  const beamTopY = cy - r * 5.5;
  // Two-side cone path: narrow top, wider bottom (origin at node center)
  const beam = `M ${cx - r * 0.65} ${cy} L ${cx + r * 0.65} ${cy} L ${cx + r * 0.18} ${beamTopY} L ${cx - r * 0.18} ${beamTopY} Z`;
  return (
    <g className="paradise-overlay" style={{ pointerEvents: 'none' }}>
      {/* Upward heavenly light beam */}
      <path d={beam} fill="url(#paradise-beam)" className="paradise-beam-anim" opacity="0.7" />
      {/* Golden halo ring */}
      <circle cx={cx} cy={cy} r={r * 1.55} fill="none" stroke="#fbbf24" strokeWidth="1.6" opacity="0.55" className="paradise-halo-anim" />
      <circle cx={cx} cy={cy} r={r * 1.85} fill="none" stroke="#fde68a" strokeWidth="0.8" opacity="0.3" strokeDasharray="3 4" />
    </g>
  );
};

// Heaven-style visual shapes (arch / portal / columns) — reusable for gates and spheres.
// Renders ONLY the decorative graphic, no text. Text is rendered by the caller.
const HeavenShape = ({ shape, cx, cy, r }) => {
  if (shape === 'arch') {
    const archPath = `M ${cx - r} ${cy + r * 1.4}
      L ${cx - r} ${cy - r * 0.3}
      Q ${cx - r} ${cy - r * 1.7} ${cx} ${cy - r * 1.7}
      Q ${cx + r} ${cy - r * 1.7} ${cx + r} ${cy - r * 0.3}
      L ${cx + r} ${cy + r * 1.4} Z`;
    const innerPath = `M ${cx - r * 0.82} ${cy + r * 1.28}
      L ${cx - r * 0.82} ${cy - r * 0.25}
      Q ${cx - r * 0.82} ${cy - r * 1.5} ${cx} ${cy - r * 1.5}
      Q ${cx + r * 0.82} ${cy - r * 1.5} ${cx + r * 0.82} ${cy - r * 0.25}
      L ${cx + r * 0.82} ${cy + r * 1.28} Z`;
    return (
      <g>
        <path d={archPath} fill="url(#paradise-gold)" stroke="#fbbf24" strokeWidth="2.2"
          filter="url(#paradise-glow)" className="paradise-gate-shimmer" />
        <path d={innerPath} fill="url(#paradise-inner)" opacity="0.85" />
        <circle cx={cx} cy={cy - r * 1.4} r={r * 0.12} fill="#fde68a" stroke="#fbbf24" strokeWidth="0.8" />
      </g>
    );
  }
  if (shape === 'portal') {
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx={r * 1.25} ry={r * 1.85} fill="url(#paradise-portal-glow)" opacity="0.9" />
        <ellipse cx={cx} cy={cy} rx={r * 0.95} ry={r * 1.55} fill="none" stroke="#fbbf24" strokeWidth="2"
          filter="url(#paradise-glow)" className="paradise-gate-shimmer" />
        <ellipse cx={cx} cy={cy} rx={r * 0.78} ry={r * 1.35} fill="url(#paradise-portal-glow)" opacity="0.7" />
        <ellipse cx={cx} cy={cy} rx={r * 0.55} ry={r * 1.05} fill="none" stroke="#fef9c3" strokeWidth="0.6" opacity="0.55" />
      </g>
    );
  }
  if (shape === 'columns') {
    const colW = r * 0.32;
    const colH = r * 2.5;
    const baseY = cy - r * 1.25;
    const stars = [
      { dx: -0.25, dy: -0.7, s: 0.06 }, { dx: 0.18, dy: -0.4, s: 0.05 },
      { dx: -0.05, dy: 0.05, s: 0.05 }, { dx: 0.32, dy: 0.45, s: 0.06 },
      { dx: -0.3, dy: 0.7, s: 0.05 },
    ];
    return (
      <g>
        <rect x={cx - r * 0.7} y={baseY} width={r * 1.4} height={colH} fill="#0b0a2e" opacity="0.85" />
        {stars.map((st, si) => (
          <circle key={si} cx={cx + r * st.dx} cy={cy + r * st.dy} r={r * st.s} fill="#fef9c3" opacity="0.85" />
        ))}
        <rect x={cx - r} y={baseY} width={colW} height={colH} fill="url(#paradise-marble)" stroke="#fbbf24" strokeWidth="0.7" />
        <rect x={cx + r - colW} y={baseY} width={colW} height={colH} fill="url(#paradise-marble)" stroke="#fbbf24" strokeWidth="0.7" />
        <rect x={cx - r - colW * 0.18} y={baseY - r * 0.12} width={colW * 1.36} height={r * 0.14}
          fill="#fde68a" stroke="#fbbf24" strokeWidth="0.6" />
        <rect x={cx + r - colW - colW * 0.18} y={baseY - r * 0.12} width={colW * 1.36} height={r * 0.14}
          fill="#fde68a" stroke="#fbbf24" strokeWidth="0.6" />
        <path d={`M ${cx - r - colW * 0.1} ${baseY - r * 0.12}
                  Q ${cx} ${baseY - r * 1.1} ${cx + r + colW * 0.1} ${baseY - r * 0.12}`}
          fill="none" stroke="#fbbf24" strokeWidth="3" filter="url(#paradise-glow)" className="paradise-gate-shimmer" />
      </g>
    );
  }
  return null;
};

const KarmaMatrix = ({
  results,
  connections: dynamicConnections,
  nodeLabels,
  nodePositions,
  nodeColors,
  nodeTextColors,
  nodeSizes,
  nodeShapes,
  globalSphereSize,
  backgroundImage,
  backgroundScale,
  backgroundOffsetX,
  backgroundOffsetY,
  backgroundLayers,
  connectionSpeed = 1,
  connectionStyle = 'straight',
  connectionColorMap = {},
  gates = null,
  gateValues = null,
  backgroundFullscreen = false,
  paradiseMode = false,
  hiddenSpheres = [],
  hiddenValues = [],
  customNodes = null,
  evaluateFormula = null,
  // "İl Enerji Uyumu" Σ (sum) labels — { [nodeId]: sumValue }
  sumValues = null,
  showSum = false,
}) => {
  const activeConnections = dynamicConnections || DEFAULT_CONNECTIONS;
  const globalSize = globalSphereSize || 22;

  // Each KarmaMatrix instance gets a stable unique ID so that its SVG-scoped
  // <defs> (gradients, filters) DON'T collide with other matrices rendered on
  // the same page (parent + variants).  Without this prefix, SVG IDs are
  // document-global — every `grad-D` on the page resolves to whichever
  // <radialGradient id="grad-D"> was defined first, so variant colour changes
  // were silently overridden by the parent matrix.
  const uid = useId().replace(/:/g, '');
  const gid = (id) => `grad-${uid}-${id}`;
  const fid = (id) => `glow-${uid}-${id}`;

  // Determine all node IDs from labels (or fallback to core)
  const allNodeIds = useMemo(() => {
    const fromLabels = nodeLabels ? Object.keys(nodeLabels) : [];
    const merged = new Set([...CORE_NODE_IDS, ...fromLabels]);
    return Array.from(merged);
  }, [nodeLabels]);

  const hiddenSet = useMemo(() => new Set(hiddenSpheres || []), [hiddenSpheres]);
  const hiddenValuesSet = useMemo(() => new Set(hiddenValues || []), [hiddenValues]);
  const isHidden = (id) => hiddenSet.has(id);
  const isValueHidden = (id) => hiddenValuesSet.has(id);

  const getPos = (id) => {
    if (nodePositions && nodePositions[id]) return { x: nodePositions[id].x, y: nodePositions[id].y };
    return DEFAULT_POSITIONS[id] || { x: 300, y: 300 };
  };
  const getColor = (id) => (nodeColors && nodeColors[id]) || DEFAULT_COLORS[id] || '#a855f7';
  const getTextColor = (id) => (nodeTextColors && nodeTextColors[id]) || '#ffffff';
  const getLabel = (id) => {
    if (nodeLabels && nodeLabels[id]) return nodeLabels[id].letter;
    return DEFAULT_LABELS[id] || id;
  };
  const getSize = (id) => {
    const override = nodeSizes && nodeSizes[id];
    const size = (override && override > 0) ? override : globalSize;
    return Math.max(5, Math.min(80, size));
  };
  const getShape = (id) => (nodeShapes && nodeShapes[id]) || 'circle';

  // Build list of background layers (use layers list if provided, else fallback to single legacy bg)
  const layers = useMemo(() => {
    if (backgroundLayers && backgroundLayers.length) return backgroundLayers;
    if (backgroundImage) {
      return [{
        filename: backgroundImage,
        scale: backgroundScale || 1,
        offset_x: backgroundOffsetX || 0,
        offset_y: backgroundOffsetY || 0,
        opacity: 1,
      }];
    }
    return [];
  }, [backgroundLayers, backgroundImage, backgroundScale, backgroundOffsetX, backgroundOffsetY]);

  const gradientDefs = useMemo(() => {
    return allNodeIds.map(id => {
      const color = getColor(id);
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      const lighter = `rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)})`;
      const darker = `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`;
      return { id, color, lighter, darker };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeColors, allNodeIds]);

  return (
    <div className="karma-matrix-container">
      <svg viewBox="0 0 600 700" className="karma-matrix-svg">
        <defs>
          {gradientDefs.map(({ id, lighter, color, darker }) => (
            <radialGradient key={`g-${id}`} id={gid(id)} cx="35%" cy="30%">
              <stop offset="0%" stopColor={lighter} />
              <stop offset="60%" stopColor={color} />
              <stop offset="100%" stopColor={darker} />
            </radialGradient>
          ))}
          {gradientDefs.map(({ id, color }) => (
            <filter key={`f-${id}`} id={fid(id)} x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feFlood floodColor={color} floodOpacity="0.35" />
              <feComposite in2="blur" operator="in" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
          <filter id="particle-glow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* ========= PARADISE / HEAVENLY GATE DEFS ========= */}
          <linearGradient id="paradise-gold" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <linearGradient id="paradise-inner" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fef9c3" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#a78bfa" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#1e1b4b" stopOpacity="0.85" />
          </linearGradient>
          <radialGradient id="paradise-portal-glow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#fffbeb" stopOpacity="0.95" />
            <stop offset="45%" stopColor="#fbbf24" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7c2d12" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="paradise-marble" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="30%" stopColor="#ffffff" />
            <stop offset="70%" stopColor="#e5e7eb" />
            <stop offset="100%" stopColor="#9ca3af" />
          </linearGradient>
          <linearGradient id="paradise-beam" x1="50%" y1="100%" x2="50%" y2="0%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.85" />
            <stop offset="50%" stopColor="#fde68a" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#fef9c3" stopOpacity="0" />
          </linearGradient>
          <filter id="paradise-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#fbbf24" floodOpacity="0.55" />
            <feComposite in2="blur" operator="in" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background layers (stacked in order) — only when not fullscreen */}
        {!backgroundFullscreen && layers.map((layer, i) => {
          const url = `${BACKEND_URL}/api/uploads/${layer.filename}`;
          const sc = layer.scale ?? 1;
          const ox = layer.offset_x ?? 0;
          const oy = layer.offset_y ?? 0;
          const op = layer.opacity ?? 1;
          const fit = layer.fit_mode || 'cover';
          // SVG preserveAspectRatio mapping
          const par = fit === 'contain' ? 'xMidYMid meet'
            : fit === 'stretch' ? 'none'
            : fit === 'natural' ? 'xMidYMid meet'  // natural uses image's own size
            : 'xMidYMid slice';  // cover (default)
          const naturalSize = fit === 'natural';
          return (
            <g key={`bg-${i}-${layer.filename}`}
               transform={`translate(${ox}, ${oy}) translate(300, 350) scale(${sc}) translate(-300, -350)`}
               opacity={op}>
              <image
                href={url}
                x={naturalSize ? 0 : 0}
                y={naturalSize ? 0 : 0}
                width={naturalSize ? undefined : 600}
                height={naturalSize ? undefined : 700}
                preserveAspectRatio={par}
                className="matrix-bg-image"
                loading="lazy"
                decoding="async"
                style={{ willChange: 'transform' }}
              />
            </g>
          );
        })}

        {/* Human wireframe (subtle guide) */}
        <HumanFigure />

        {/* Nerve connections */}
        {activeConnections.map(([fromId, toId], i) => {
          const from = getPos(fromId);
          const to = getPos(toId);
          const key = fromId < toId ? `${fromId}|${toId}` : `${toId}|${fromId}`;
          const groupColor = connectionColorMap[key];
          // If part of an active group, both endpoints use the group color (more vibrant + thicker)
          const c1 = groupColor || getColor(fromId);
          const c2 = groupColor || getColor(toId);
          return (
            <NerveLine
              key={`nerve-${i}-${fromId}-${toId}`}
              x1={from.x} y1={from.y}
              x2={to.x} y2={to.y}
              index={i}
              color1={c1}
              color2={c2}
              speed={connectionSpeed}
              style={connectionStyle}
              isGrouped={!!groupColor}
            />
          );
        })}

        {/* Spheres / shapes */}
        {allNodeIds.map((id) => {
          const pos = getPos(id);
          const size = getSize(id);
          const shape = getShape(id);
          const color = getColor(id);
          const mainVal = results ? results[id] : null;
          const hasValue = mainVal !== null && mainVal !== undefined && !isValueHidden(id);

          // Σ label — under each sphere when sum mode is active and the id
          // has a computed sum. Small yellow text; skipped when the sphere
          // itself is hidden or its value is suppressed.
          const sumVal = (showSum && sumValues && typeof sumValues[id] === 'number')
            ? sumValues[id]
            : null;
          const sumLabel = (!isHidden(id) && !isValueHidden(id) && sumVal !== null) ? (
            <text
              x={pos.x}
              y={pos.y + size * 1.15}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#fde68a"
              fontSize={Math.max(7, size * 0.38)}
              fontWeight="700"
              fontFamily="'Inter', sans-serif"
              data-testid={`matrix-sum-${id}`}
              style={{
                pointerEvents: 'none',
                paintOrder: 'stroke',
                stroke: 'rgba(0,0,0,0.75)',
                strokeWidth: 0.6,
                filter: 'drop-shadow(0 0 3px rgba(251,191,36,0.55))',
              }}
            >
              Σ = {sumVal}
            </text>
          ) : null;

          // Special rendering for SUN node
          const addKey = `${id}1`;
          const addVal = results ? results[addKey] : null;
          const hasAddValue = addVal !== null && addVal !== undefined && !isValueHidden(id);
          const dualDisplay = hasValue && hasAddValue
            ? `${mainVal} / ${addVal}`
            : (hasValue ? `${mainVal}` : null);

          if (id === 'SUN') {
            return (
              <g key={id} className="sphere-group-subtle matrix-sun-group" data-testid={`matrix-node-${id}`}>
                {!isHidden(id) && paradiseMode && <ParadiseOverlay cx={pos.x} cy={pos.y} r={size} />}
                {!isHidden(id) && (
                  <>
                    {/* Outer glow halo */}
                    <circle cx={pos.x} cy={pos.y} r={size * 2.2} fill={color} opacity="0.08" />
                    <circle cx={pos.x} cy={pos.y} r={size * 1.7} fill={color} opacity="0.12" />
                    {/* Rotating rays */}
                    <g className="matrix-sun-rays" style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}>
                      {Array.from({ length: 12 }).map((_, i) => {
                        const a = (Math.PI / 6) * i;
                        const r1 = size * 1.15;
                        const r2 = size * 1.65;
                        const x1 = pos.x + Math.cos(a) * r1;
                        const y1 = pos.y + Math.sin(a) * r1;
                        const x2 = pos.x + Math.cos(a) * r2;
                        const y2 = pos.y + Math.sin(a) * r2;
                        return (
                          <line key={i}
                            x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke={color} strokeWidth="1.5" opacity="0.6" strokeLinecap="round"
                          />
                        );
                      })}
                    </g>
                    {/* Pulsing ring */}
                    <circle cx={pos.x} cy={pos.y} r={size * 1.35} fill="none" stroke={color} strokeWidth="0.8" opacity="0.3" className="matrix-sun-pulse" />
                    {/* Main core with gradient */}
                    <circle
                      cx={pos.x} cy={pos.y} r={size}
                      fill={`url(#${gid(id)})`}
                      filter={`url(#${fid(id)})`}
                      className="sphere-subtle matrix-sun-core"
                    />
                    {/* Highlight */}
                    <circle cx={pos.x - size * 0.3} cy={pos.y - size * 0.3} r={size * 0.3} fill="white" opacity="0.25" />
                  </>
                )}
                {/* Label / value — fully suppressed when the sphere is hidden */}
                {!isHidden(id) && (hasValue ? (
                  <>
                    <text x={pos.x} y={pos.y - size * 0.27} textAnchor="middle" dominantBaseline="central"
                      fill={getTextColor(id)} fontSize={Math.max(8, size * 0.5)} fontWeight="700" fontFamily="'Cinzel', serif"
                      opacity="0.95" style={{ pointerEvents: 'none' }}>
                      {getLabel(id)}
                    </text>
                    <text x={pos.x} y={pos.y + size * 0.36} textAnchor="middle" dominantBaseline="central"
                      fill={getTextColor(id)} fontSize={Math.max(8, size * (hasAddValue ? 0.42 : 0.6))} fontWeight="700" fontFamily="'Inter', sans-serif"
                      style={{ pointerEvents: 'none' }}>
                      {dualDisplay}
                    </text>
                  </>
                ) : (
                  <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                    fill={getTextColor(id)} fontSize={Math.max(12, size * 0.75)} fontWeight="700"
                    style={{ pointerEvents: 'none' }}>
                    {getLabel(id)}
                  </text>
                ))}
                {sumLabel}
              </g>
            );
          }

          // Heaven-style shapes for spheres (arch / portal / columns) — render gate-like graphic + letter + dual value
          if (shape === 'arch' || shape === 'portal' || shape === 'columns') {
            const textColor = getTextColor(id);
            const useGoldText = textColor === '#ffffff';
            const letterColor = useGoldText ? '#fbbf24' : textColor;
            const valueColor = useGoldText ? '#fffbeb' : textColor;
            return (
              <g key={id} className="sphere-group-subtle" data-testid={`matrix-node-${id}`}>
                {!isHidden(id) && paradiseMode && <ParadiseOverlay cx={pos.x} cy={pos.y} r={size} />}
                {!isHidden(id) && <HeavenShape shape={shape} cx={pos.x} cy={pos.y} r={size} />}
                {!isHidden(id) && (hasValue ? (
                  <>
                    <text x={pos.x} y={pos.y - size * 0.5} textAnchor="middle" dominantBaseline="central"
                      fill={letterColor} fontSize={Math.max(8, size * 0.45)} fontWeight="700" fontFamily="'Cinzel', serif"
                      style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.85))' }}>
                      {getLabel(id)}
                    </text>
                    <text x={pos.x} y={pos.y + size * 0.4} textAnchor="middle" dominantBaseline="central"
                      fill={valueColor} fontSize={Math.max(8, size * (hasAddValue ? 0.5 : 0.85))} fontWeight="800" fontFamily="'Inter', sans-serif"
                      style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.7))' }}>
                      {dualDisplay}
                    </text>
                  </>
                ) : (
                  <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                    fill={letterColor} fontSize={Math.max(9, size * 0.6)} fontWeight="700" fontFamily="'Cinzel', serif"
                    style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.85))' }}>
                    {getLabel(id)}
                  </text>
                ))}
                {sumLabel}
              </g>
            );
          }

          return (
            <g key={id} className="sphere-group-subtle" data-testid={`matrix-node-${id}`}>
              {!isHidden(id) && paradiseMode && <ParadiseOverlay cx={pos.x} cy={pos.y} r={size} />}
              {!isHidden(id) && (
                <>
                  {/* Ambient glow circle (always circle for glow effect) */}
                  <circle cx={pos.x} cy={pos.y} r={size * 1.45} fill={color} opacity="0.05" className="sphere-ambient" />
                  {/* Outer ring — same shape as node */}
                  <NodeShape shape={shape} cx={pos.x} cy={pos.y} r={size * 1.27} fill="none" stroke={color} strokeWidth={0.8} opacity={0.2} />
                  {/* Main shape with gradient + glow */}
                  <NodeShape
                    shape={shape}
                    cx={pos.x} cy={pos.y} r={size}
                    fill={`url(#${gid(id)})`}
                    filter={`url(#${fid(id)})`}
                    className="sphere-subtle"
                  />
                  {/* Highlight circle */}
                  <circle cx={pos.x - size * 0.32} cy={pos.y - size * 0.32} r={size * 0.32} fill="white" opacity="0.1" />
                </>
              )}

              {!isHidden(id) && (hasValue ? (
                <>
                  <text x={pos.x} y={pos.y - size * 0.27} textAnchor="middle" dominantBaseline="central"
                    fill={getTextColor(id)} fontSize={Math.max(7, size * 0.45)} fontWeight="600" fontFamily="'Cinzel', serif"
                    opacity="0.85" style={{ pointerEvents: 'none' }}>
                    {getLabel(id)}
                  </text>
                  <text x={pos.x} y={pos.y + size * 0.36} textAnchor="middle" dominantBaseline="central"
                    fill={getTextColor(id)} fontSize={Math.max(8, size * (hasAddValue ? 0.42 : 0.6))} fontWeight="700" fontFamily="'Inter', sans-serif"
                    style={{ pointerEvents: 'none' }}>
                    {dualDisplay}
                  </text>
                </>
              ) : (
                <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                  fill={getTextColor(id)} fontSize={Math.max(9, size * 0.63)} fontWeight="700" fontFamily="'Cinzel', serif"
                  style={{ pointerEvents: 'none' }}>
                  {getLabel(id)}
                </text>
              ))}
              {sumLabel}
            </g>
          );
        })}

        {/* Gates (4 left + 4 right with shape + value source per gate) */}
        {gates && gates.enabled && (
          <g className="matrix-gates" data-testid="matrix-gates">
            {(() => {
              const baseSize = gates.size || 28;
              const color = gates.color || '#fbbf24';
              const shapes = gates.shapes || [];
              const sizes = gates.sizes || [];
              const sources = gates.value_sources || [];

              const resolveValue = (i) => {
                const src = sources[i] || 'auto';
                if (src === 'auto') {
                  return gateValues ? gateValues[i] : null;
                }
                if (results && Object.prototype.hasOwnProperty.call(results, src)) {
                  return results[src];
                }
                return null;
              };

              const renderOne = (side, pos, i) => {
                if (!pos) return null;
                // Skip hidden gates entirely (per-side, per-index)
                const hiddenArr = side === 'left' ? (gates.hidden_left || []) : (gates.hidden_right || []);
                if (hiddenArr[i]) return null;
                const shape = shapes[i] || 'door';
                const r = sizes[i] || baseSize;
                const nameArr = side === 'left' ? (gates.hide_names || []) : (gates.hide_names || []);
                const valArr = side === 'left' ? (gates.hide_values || []) : (gates.hide_values || []);
                const nameHidden = !!nameArr[i];
                const valueHidden = !!valArr[i];
                const rawName = (gates.names || [])[i] || `Q${i + 1}`;
                const name = nameHidden ? '' : rawName;
                const rawVal = resolveValue(i);
                const hasRawValRaw = rawVal !== null && rawVal !== undefined;
                const hasRawVal = hasRawValRaw && !valueHidden;
                // 4th gate (index 3) — display value as infinity symbol, but ONLY after calculation
                const isInfinityGate = i === 3;
                const val = isInfinityGate ? '∞' : rawVal;
                const hasVal = isInfinityGate ? hasRawVal : hasRawVal;
                const infinityScale = isInfinityGate ? (gates.infinity_scale || 1.0) : 1.0;
                const testId = `gate-${side}-${i}`;
                const overlay = paradiseMode && shape !== 'none'
                  ? <ParadiseOverlay cx={pos.x} cy={pos.y} r={r} />
                  : null;

                // Arch — gothic cathedral arched gate (classic)
                if (shape === 'arch') {
                  return (
                    <g key={testId} data-testid={testId}>
                      {overlay}
                      <HeavenShape shape="arch" cx={pos.x} cy={pos.y} r={r} />
                      <text x={pos.x} y={pos.y - r * 0.55} textAnchor="middle" dominantBaseline="central"
                        fill="#fbbf24" fontSize={r * 0.4} fontWeight="700" fontFamily="'Cinzel', serif"
                        style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.85))' }}>
                        {name}
                      </text>
                      {hasVal && (
                        <text x={pos.x} y={pos.y + r * 0.4} textAnchor="middle" dominantBaseline="central"
                          fill="#fffbeb" fontSize={r * 0.9 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.7))' }}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                }

                // Portal — glowing oval light portal
                if (shape === 'portal') {
                  return (
                    <g key={testId} data-testid={testId}>
                      {overlay}
                      <HeavenShape shape="portal" cx={pos.x} cy={pos.y} r={r} />
                      <text x={pos.x} y={pos.y - r * 0.5} textAnchor="middle" dominantBaseline="central"
                        fill="#fbbf24" fontSize={r * 0.4} fontWeight="700" fontFamily="'Cinzel', serif"
                        style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))' }}>
                        {name}
                      </text>
                      {hasVal && (
                        <text x={pos.x} y={pos.y + r * 0.35} textAnchor="middle" dominantBaseline="central"
                          fill="#fffbeb" fontSize={r * 0.95 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 5px rgba(251,191,36,0.85))' }}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                }

                // Columns — marble columns + golden arch + starry interior
                if (shape === 'columns') {
                  return (
                    <g key={testId} data-testid={testId}>
                      {overlay}
                      <HeavenShape shape="columns" cx={pos.x} cy={pos.y} r={r} />
                      <text x={pos.x} y={pos.y - r * 0.55} textAnchor="middle" dominantBaseline="central"
                        fill="#fbbf24" fontSize={r * 0.36} fontWeight="700" fontFamily="'Cinzel', serif"
                        style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.85))' }}>
                        {name}
                      </text>
                      {hasVal && (
                        <text x={pos.x} y={pos.y + r * 0.45} textAnchor="middle" dominantBaseline="central"
                          fill="#fffbeb" fontSize={r * 0.85 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.75))' }}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                }

                // Door shape: tall door rectangle
                if (shape === 'door') {
                  return (
                    <g key={testId} data-testid={testId}>
                      {overlay}
                      <rect x={pos.x - r} y={pos.y - r * 1.4} width={r * 2} height={r * 2.8}
                        rx={r * 0.18} fill={`url(#${gid('SUN')})`} stroke={color} strokeWidth="1.5"
                        opacity="0.92" filter={`url(#${fid('SUN')})`} />
                      <rect x={pos.x - r * 0.85} y={pos.y - r * 1.25} width={r * 1.7} height={r * 2.5}
                        rx={r * 0.12} fill="rgba(0,0,0,0.45)" stroke={color} strokeWidth="0.8" opacity="0.7" />
                      <text x={pos.x} y={pos.y - r * 0.55} textAnchor="middle" dominantBaseline="central"
                        fill={color} fontSize={r * 0.4} fontWeight="600" fontFamily="'Cinzel', serif"
                        style={{ pointerEvents: 'none' }}>
                        {name}
                      </text>
                      {hasVal && (
                        <text x={pos.x} y={pos.y + r * 0.4} textAnchor="middle" dominantBaseline="central"
                          fill="white" fontSize={r * 0.85 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                          style={{ pointerEvents: 'none' }}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                }
                // No shape: only text labels
                if (shape === 'none') {
                  return (
                    <g key={testId} data-testid={testId}>
                      {name && (
                        <text x={pos.x} y={pos.y - r * 0.5} textAnchor="middle" dominantBaseline="central"
                          fill={color} fontSize={r * 0.45} fontWeight="700" fontFamily="'Cinzel', serif"
                          style={{ pointerEvents: 'none', textShadow: '0 0 8px rgba(0,0,0,0.7)' }}>
                          {name}
                        </text>
                      )}
                      {hasVal && (
                        <text x={pos.x} y={pos.y + (name ? r * 0.4 : 0)} textAnchor="middle" dominantBaseline="central"
                          fill="white" fontSize={r * 1.05 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                          style={{ pointerEvents: 'none', filter: `drop-shadow(0 0 6px ${color})` }}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                }
                // Other shapes (circle/square/star/etc.) — use NodeShape primitive
                return (
                  <g key={testId} data-testid={testId}>
                    {overlay}
                    <NodeShape shape={shape} cx={pos.x} cy={pos.y} r={r * 1.05} fill="none" stroke={color} strokeWidth={0.8} opacity={0.25} />
                    <NodeShape shape={shape} cx={pos.x} cy={pos.y} r={r}
                      fill={`url(#${gid('SUN')})`} stroke={color} strokeWidth="1.4"
                      filter={`url(#${fid('SUN')})`} />
                    {name && (
                      <text x={pos.x} y={pos.y - r * 0.32} textAnchor="middle" dominantBaseline="central"
                        fill={color} fontSize={r * 0.36} fontWeight="700" fontFamily="'Cinzel', serif"
                        style={{ pointerEvents: 'none' }}>
                        {name}
                      </text>
                    )}
                    {hasVal && (
                      <text x={pos.x} y={pos.y + r * 0.3} textAnchor="middle" dominantBaseline="central"
                        fill="white" fontSize={r * 0.7 * infinityScale} fontWeight="800" fontFamily="'Inter', sans-serif"
                        style={{ pointerEvents: 'none' }}>
                        {val}
                      </text>
                    )}
                  </g>
                );
              };

              const out = [];
              if (gates.show_left !== false) {
                (gates.positions_left || []).forEach((p, i) => out.push(renderOne('left', p, i)));
              }
              if (gates.show_right !== false) {
                (gates.positions_right || []).forEach((p, i) => out.push(renderOne('right', p, i)));
              }
              return out;
            })()}
          </g>
        )}

        {/* Custom computed nodes — admin-defined formula circles.
            NOTE: We pre-compute all custom node values in dependency order
            so that later custom nodes can reference earlier ones by label —
            e.g. a "Kalp" node with formula `Man+Man+A` becomes a usable
            variable `Kalp` in another custom node's formula.  Multi-pass to
            resolve dependencies (nodes not yet computed keep waiting until
            their dependencies resolve). */}
        {(() => {
          if (!customNodes || customNodes.length === 0 || !evaluateFormula || !results) return null;
          const extended = { ...results };
          const perNode = {};
          const maxPasses = customNodes.length + 2;
          for (let pass = 0; pass < maxPasses; pass++) {
            let changed = false;
            for (const cn of customNodes) {
              if (perNode[cn.id]) continue;
              let mainVal = null, addVal = null;
              if (cn.show_pair !== false && evaluateFormula.pair) {
                const pair = evaluateFormula.pair(cn.formula, extended);
                if (pair) { mainVal = pair.main; addVal = pair.add; }
              } else {
                mainVal = evaluateFormula(cn.formula, extended, { reduce: cn.reduce !== false });
              }
              if (mainVal === null || mainVal === undefined) continue;
              perNode[cn.id] = { mainVal, addVal };
              // Register this node's label as a formula variable for later
              // custom nodes to reference (e.g., "Kalp+A").  Skip labels with
              // characters the formula whitelist would strip.
              const label = String(cn.label || '').trim();
              if (label && /^[A-Za-z][A-Za-z0-9]*$/.test(label)) {
                extended[label] = mainVal;
                if (addVal !== null && addVal !== undefined) {
                  extended[`${label}1`] = addVal;
                }
              }
              changed = true;
            }
            if (!changed) break;
          }
          return (
          <g className="custom-nodes" data-testid="custom-nodes">
            {customNodes.map((cn) => {
              const pos = cn.position || { x: 300, y: 350 };
              const r = cn.size || globalSphereSize || 22;
              const shape = cn.shape || 'circle';
              const color = cn.color || '#a855f7';
              const computed = perNode[cn.id] || {};
              const mainVal = computed.mainVal ?? null;
              const addVal = computed.addVal ?? null;
              const hasMain = mainVal !== null && mainVal !== undefined && !isValueHidden(cn.id);
              const hasAdd = addVal !== null && addVal !== undefined && !isValueHidden(cn.id);
              const dualDisplay = hasMain && hasAdd
                ? `${mainVal} / ${addVal}`
                : (hasMain ? `${mainVal}` : null);
              return (
                <g key={`custom-${cn.id}`} className="sphere-group-subtle" data-testid={`custom-node-${cn.id}`}>
                  {!isHidden(cn.id) && paradiseMode && <ParadiseOverlay cx={pos.x} cy={pos.y} r={r} />}
                  {!isHidden(cn.id) && (shape === 'arch' || shape === 'portal' || shape === 'columns') && (
                    <HeavenShape shape={shape} cx={pos.x} cy={pos.y} r={r} />
                  )}
                  {!isHidden(cn.id) && shape !== 'arch' && shape !== 'portal' && shape !== 'columns' && (
                    <>
                      <circle cx={pos.x} cy={pos.y} r={r * 1.6} fill={color} opacity="0.18" className="sphere-ambient" />
                      <NodeShape
                        shape={shape}
                        cx={pos.x}
                        cy={pos.y}
                        r={r}
                        fill={color}
                        stroke="white"
                        strokeWidth={0.6}
                        className="sphere-subtle"
                        style={{ filter: `drop-shadow(0 0 10px ${color}66)` }}
                      />
                    </>
                  )}
                  {!isHidden(cn.id) && cn.label && (
                    <text
                      x={pos.x}
                      y={dualDisplay ? pos.y - r * 0.36 : pos.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill={cn.text_color || '#ffffff'}
                      fontSize={Math.max(9, r * (dualDisplay ? 0.42 : 0.7))}
                      fontWeight="700"
                      fontFamily="'Cinzel', serif"
                      style={{ pointerEvents: 'none' }}
                    >
                      {cn.label}
                    </text>
                  )}
                  {!isHidden(cn.id) && dualDisplay && (
                    <text
                      x={pos.x}
                      y={cn.label ? pos.y + r * 0.36 : pos.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill={cn.text_color || '#ffffff'}
                      fontSize={Math.max(8, r * (hasAdd ? 0.42 : 0.6))}
                      fontWeight="800"
                      fontFamily="'Inter', sans-serif"
                      style={{ pointerEvents: 'none' }}
                    >
                      {dualDisplay}
                    </text>
                  )}
                  {/* Σ (sum) label — only when sum mode active and this custom
                      node has a computed sum from both dates */}
                  {!isHidden(cn.id) && showSum && sumValues && typeof sumValues[cn.id] === 'number' && (
                    <text
                      x={pos.x}
                      y={pos.y + r * 1.15}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="#fde68a"
                      fontSize={Math.max(7, r * 0.38)}
                      fontWeight="700"
                      fontFamily="'Inter', sans-serif"
                      data-testid={`custom-node-sum-${cn.id}`}
                      style={{
                        pointerEvents: 'none',
                        paintOrder: 'stroke',
                        stroke: 'rgba(0,0,0,0.75)',
                        strokeWidth: 0.6,
                        filter: 'drop-shadow(0 0 3px rgba(251,191,36,0.55))',
                      }}
                    >
                      Σ = {sumValues[cn.id]}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
          );
        })()}
      </svg>
    </div>
  );
};

export { NodeShape };
export default KarmaMatrix;
